import type { QueryObserverResult } from "@tanstack/react-query";
import type { Read, Result } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import { admissionsCopy as c } from "./admissions-copy";

export const imam = { system: "imam" } as const;
export const confirmed = (effects: string[], version?: string): Result => ({ state: "confirmed", at: new Date().toISOString(), effects, remains: [], ...(version ? { version } : {}) });
export const refused = (sentence: string): Result => ({ state: "refused", cap: c.notRecorded, sentence, draft: "kept" });
export function read<T>(query: Pick<QueryObserverResult<T>, "data" | "isError" | "dataUpdatedAt" | "refetch">, label: string): Read<T> {
  if (query.data !== undefined) return query.isError
    ? { status: "stale", last: query.data, readAt: new Date(query.dataUpdatedAt).toISOString(), sentence: c.staleRead(label), refresh: () => void query.refetch() }
    : { status: "ready", value: query.data, readAt: new Date(query.dataUpdatedAt).toISOString() };
  return query.isError ? { status: "failed", sentence: c.readFailed(label), retry: () => void query.refetch() } : { status: "loading" };
}
export function recover<T>(key: string, settle: (result: T) => Promise<Result> | Result): Result {
  return { state: "unknown", cap: c.notConfirmed, sentence: c.unknown, requestId: key,
    check: { verb: c.check, note: c.checkNote, run: async () => {
      try {
        const result = await checkCommit<T>(key, value => value as T);
        if (result.state === "confirmed" || result.state === "partial") return settle(result.receipt.result);
        if (result.state === "refused") return refused((result.receipt.result as { reason?: { message?: string } }).reason?.message ?? c.noCommit);
        if (result.state === "not_committed") return refused(c.noCommit);
      } catch { /* An unreadable receipt proves no outcome. */ }
      return recover(key, settle);
    } } };
}
export async function send<T>(key: string, run: () => Promise<T>, settle: (result: T) => Promise<Result> | Result, conflict?: (error: unknown) => void): Promise<Result> {
  try { return await settle(await run()); }
  catch (error) {
    if (error instanceof ApiError && error.status < 500) {
      conflict?.(error);
      return refused(reasonOnError(error)?.message ?? error.message);
    }
    return recover(key, settle);
  }
}
